import type { Dispatch, SetStateAction } from "react";
import { useEffect, useMemo, useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Dropdown, EllipsisDropdownItem } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import {
    DESCRIPTION_ID,
    sources,
    SOURCES_POPUP_HEIGHT,
    SOURCES_POPUP_WIDTH,
} from "app/pages/activation/components/RankWidget/config";
import { DropdownButton } from "app/pages/activation/components/RankWidget/CreationModal/Components/DropdownButton/DropdownButton";
import type { RTFormData } from "app/pages/activation/components/RankWidget/types";
import { activationTracking } from "app/pages/activation/services/tracking";
import type { DEVICES_TYPES_FOR_API } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

type SourceSchema = {
    setFormData?: Dispatch<SetStateAction<RTFormData>>;
    dataAutomation?: string;
};

export const SourceDropdown = ({ setFormData, dataAutomation }: SourceSchema) => {
    const [selectedSource, setSelectedSource] = useState(sources[0].id);
    const { rankTracker } = activationTracking;

    const selectedSourceText = sources.find((source) => source.id === selectedSource)?.text || "";

    useEffect(() => {
        setFormData?.((prevState) => ({ ...prevState, source: selectedSource }));
    }, [selectedSource, setFormData]);

    const getItems = useMemo(
        () =>
            sources.map(({ id, text }) => (
                <EllipsisDropdownItem
                    key={id}
                    id={id}
                    text={translate(text)}
                    selected={selectedSource === id}
                >
                    {translate(text)}
                </EllipsisDropdownItem>
            )),
        [selectedSource],
    );

    const onClick = ({ id }: { id: DEVICES_TYPES_FOR_API }) => {
        if (id) {
            setSelectedSource(id);
        }
    };

    const onToggle = (isOpen: boolean) => {
        if (!isOpen) {
            rankTracker.trackChangeSource(selectedSource);
        }
    };

    return (
        <Dropdown
            width={SOURCES_POPUP_WIDTH}
            dropdownPopupHeight={SOURCES_POPUP_HEIGHT}
            appendTo={`#${DESCRIPTION_ID}`}
            dropdownPopupPlacement="bottom-left"
            onClick={onClick}
            closeOnItemClick
            buttonWidth="max-content"
            onToggle={onToggle}
        >
            {[
                <DropdownButton
                    dataAutomation={dataAutomation}
                    color={colorsPalettes.carbon[400]}
                    key="source-dropdown"
                >
                    {translate(selectedSourceText)}
                </DropdownButton>,
                ...getItems,
            ]}
        </Dropdown>
    );
};
